import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlertService } from '@ddv/common-components';
import { ExecutorService } from '@ddv/http';
import { NO_ENTITLEMENTS, Query } from '@ddv/models';
import { replaceHostInMetadataAndQueryURLsToken, trebekApiLocationToken } from '@ddv/named-queries';
import { catchError, concatMap, filter, map, Observable, of, repeat, switchMap, takeWhile, tap, throwError } from 'rxjs';

import { TrebekQueryMappingService } from './trebek-query-mapping.service';
import {
    PublicAPIQueryResponse,
    PublicApiResponseRow,
    TrebekQueryResponse,
    TrebekQueryResponseRow,
    TrebekError,
    NotEntitledToHedgeInvestorDataError,
    GenericTrebekError,
    isQueryNotReady,
    isQueryFailed,
    QueryResultResponse,
    QueryFailedResponse,
    QueryDoneResponse,
} from '../models/trebek';
import { buildErrorAlertMessage } from '../utils/adhoc-trebek-errors';

const trebekPollingIntervalInMs = 1000;

type ErrorLike = { errorCode?: string, errorMessage?: string } | string | undefined;

@Injectable()
export class DatasetTrebekQueryRunnerService {
    private readonly replaceHostInURLs = inject(replaceHostInMetadataAndQueryURLsToken);
    private readonly trebekApiLocation = inject(trebekApiLocationToken);
    private readonly executor = inject(ExecutorService);
    private readonly trebekQueryMappingService = inject(TrebekQueryMappingService);
    private readonly alertService = inject(AlertService);

    runQuery(
        widgetIds: number[],
        dataQuery: Query,
        queryEndpoint: string,
        statusEndpoint: string,
        multiClientCodes: string[] | undefined,
    ): Observable<PublicAPIQueryResponse> {
        const route = finalizeRoute(queryEndpoint, multiClientCodes);
        const isAdHocQueryType = queryEndpoint.includes('adhoc/');

        return this.callTrebek(
            route,
            dataQuery,
            statusEndpoint,
        ).pipe(
            switchMap((response: HttpResponse<TrebekQueryResponse>) => this.processResponse(response, isAdHocQueryType)),
            tap((data: PublicAPIQueryResponse) => this.updateTrebekIds(data.queryId, widgetIds)),
            catchError((error) => this.processError(error as (HttpErrorResponse | GenericTrebekError))),
        );
    }

    private callTrebek(
        route: string,
        dataQuery: Query,
        statusEndpoint: string,
    ): Observable<HttpResponse<TrebekQueryResponse>> {
        return this.getQueryRequestId(
            route,
            dataQuery,
        ).pipe(
            switchMap((response: HttpResponse<TrebekQueryResponse>) => {
                const requestId = response.body?.queryId;
                if (!requestId) {
                    throw createError('Failed to get queryId.');
                }
                const statusRoute = buildStatusRoute(statusEndpoint, requestId);

                return of(null).pipe(
                    concatMap(() => this.getQueryStatus(statusRoute)),
                    repeat({ delay: trebekPollingIntervalInMs }),
                    takeWhile((res) => isQueryNotReady(res.body), true),
                    filter((res) => !isQueryNotReady(res.body)),
                );
            }),
        );
    }

    private getQueryRequestId(
        route: string,
        query: Query,
    ): Observable<HttpResponse<TrebekQueryResponse>> {
        return this.executor.invokeServiceWithBodyAndReturnFullResponse<TrebekQueryResponse>(
            route,
            'POST',
            query);
    }

    private getQueryStatus(route: string): Observable<HttpResponse<TrebekQueryResponse>> {
        return this.executor.invokeServiceWithParamsAndReturnFullResponse<TrebekQueryResponse>(route, undefined);
    }

    private updateTrebekIds(queryId: string | undefined, widgetIds: number[]): void {
        if (queryId) {
            this.trebekQueryMappingService.updateTrebekQueryIdWidgetMappings(widgetIds, queryId);
        }
    }

    private processResponse(response: HttpResponse<TrebekQueryResponse>, isAdHocQueryType: boolean): Observable<PublicAPIQueryResponse> {
        if (isQueryFailed(response.body)) {
            if (isAdHocQueryType) {
                const errorMessage = buildErrorAlertMessage(response.body.error);
                if (errorMessage) {
                    this.alertService.error(errorMessage);
                }
            }
            return throwError(() => createError((response.body as QueryFailedResponse)?.error));
        }

        if (isQueryNotReady(response.body)) {
            return throwError(() => createError('Query is not ready yet.'));
        }

        const responseBody = response.body as QueryDoneResponse;
        if (responseBody?.url) {
            return this.executor.invokeServiceWithParamsAndReturnFullResponse<QueryResultResponse>(
                this.buildResultsRoute(responseBody.url),
            ).pipe(
                map((res) => {
                    return {
                        queryId: responseBody?.queryId,
                        data: translateTrebekResponseRowsToPublicApiResponseRows(res.body?.data ?? []),
                    };
                }));
        }

        return throwError(() => createError('No URL to fetch the query results.'));
    }

    private processError(response: HttpErrorResponse | GenericTrebekError): Observable<never> {
        const error = response instanceof HttpErrorResponse ? createError((response.error || response.message) as ErrorLike) : response;

        console.error(response);

        return throwError(() => error);
    }

    private buildResultsRoute(url: string): string {
        if (this.replaceHostInURLs) { // we are not getting a proper URL to Trebek for localhost, we need to adjust
            if (url.startsWith('https://s3.us-east-2.amazonaws.com/')) {
                return url.replace('https://s3.us-east-2.amazonaws.com/', '/s3-east-2/');
            } else if (url.startsWith('https://s3.us-east-1.amazonaws.com/')) {
                return url.replace('https://s3.us-east-1.amazonaws.com/', '/s3-east-1/');
            } else {
                const pathname = new URL(url).pathname;
                const resultsEndpointPath = pathname.substring(pathname.indexOf('rest'), pathname.length);
                return `${this.trebekApiLocation}/trebek/api/${resultsEndpointPath}`;
            }
        }
        return url;
    }
}

function buildStatusRoute(statusEndpoint: string, requestId: string): string {
    return statusEndpoint.replace('{queryId}', requestId);
}

function createError(error: ErrorLike): TrebekError {
    if (isNoInvestorDataEntitlementError(error)) {
        return new NotEntitledToHedgeInvestorDataError(error);
    }

    return new GenericTrebekError(error);
}

function isNoInvestorDataEntitlementError(error: ErrorLike): boolean {
    return typeof error !== 'string' &&
        error?.errorCode === NO_ENTITLEMENTS.CODE &&
        error.errorMessage === NO_ENTITLEMENTS.TEXT;
}

function finalizeRoute(queryEndpoint: string, multiClientCodes: string[] | undefined): string {
    let route = queryEndpoint;

    if (multiClientCodes) {
        route += `?clients=${multiClientCodes.join(',')}`;
    }

    return route;
}

export function translateTrebekResponseRowsToPublicApiResponseRows(data: TrebekQueryResponseRow[]): PublicApiResponseRow[] {
    return data.map((row) => {
        const processed: PublicApiResponseRow = {
            ...row.selectedColumnData,
            links: row.links ?? {},
        } as PublicApiResponseRow;

        if (Object.prototype.hasOwnProperty.call(processed, 'date') && !Object.prototype.hasOwnProperty.call(processed, 'Date')) {
            processed.Date = processed.date;
        }

        return processed;
    });
}
